import { Injectable } from '@angular/core';
import { GridService } from '../shared/grid/grid.service';
import { HttpClient } from '@angular/common/http';
import { ControlsSummaryPayload, RiskRegister, RiskRegisterControl } from '../models/risk-assessment.model';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { FileAttachment } from '../models/fileAttachment.model';
import { RiskRegisterAttachmentRequest } from '../models/risk-register-attachment.model';
import {
  BayesianAnalysisRunRequest,
  BayesianAnalysisRunResponse,
  BayesianAnalysisStoredResultResponse,
} from '../models/bayesian-analysis.model';
import { RiskRegisterBulkUpdateRequest, RiskRegisterBulkUpdateResponse } from '../models/risk-register-bulk-update.model';
import { ToastService } from './toast.service';

@Injectable({
  providedIn: 'root'
})
export class RiskRegisterService extends GridService<RiskRegister> {

  constructor(http: HttpClient, private toastService: ToastService) {
    super('RiskRegister', http);
  }

  getRiskRegister(id: any): Observable<RiskRegister> {
    return this.http.get<RiskRegister>(this.getApiUrl(`${this.controllerName}/GetRiskRegister?id=${id}`));
  }

  getControlsSummary(payload: ControlsSummaryPayload): Observable<RiskRegisterControl[]> {
    return this.http.post<RiskRegisterControl[]>(this.getApiUrl(`${this.controllerName}/GetControlsSummary`), payload);
  }

  bulkUpdate(request: RiskRegisterBulkUpdateRequest): Observable<RiskRegisterBulkUpdateResponse> {
    return this.http.patch<RiskRegisterBulkUpdateResponse>(this.getApiUrl(`${this.controllerName}/BulkUpdate`), request);
  }

  getAttachments(riskRegisterId: string): Observable<FileAttachment[]> {
    return this.http
      .get<FileAttachment[] | null>(this.getApiUrl(`${this.controllerName}/GetAttachments`), { params: { riskRegisterId } })
      .pipe(
        map((attachments) => attachments ?? []),
        catchError((error) => {
          this.toastService.showError(error);
          return of([]);
        }),
      );
  }

  addAttachment(request: RiskRegisterAttachmentRequest): Observable<FileAttachment> {
    return this.http.post<FileAttachment>(this.getApiUrl(`${this.controllerName}/AddAttachment`), request);
  }

  removeAttachment(request: RiskRegisterAttachmentRequest): Observable<void> {
    return this.http.delete<void>(this.getApiUrl(`${this.controllerName}/RemoveAttachment`), {
      params: { riskRegisterId: request.riskRegisterId, fileAttachmentId: request.fileAttachmentId },
    });
  }

  runBayesianAnalysis(riskRegisterId: string, priorModeOverride?: number): Observable<BayesianAnalysisRunResponse> {
    const payload: BayesianAnalysisRunRequest = { riskRegisterId, ...(priorModeOverride != null ? { priorModeOverride } : {}) };
    return this.http.post<BayesianAnalysisRunResponse>(this.getApiUrl(`BayesianAnalysis/Run`), payload);
  }

  getStoredBayesianResult(riskRegisterId: string): Observable<BayesianAnalysisStoredResultResponse | null> {
    return this.http
      .get<BayesianAnalysisStoredResultResponse | null>(
        this.getApiUrl(`BayesianAnalysis/GetStoredResult?riskRegisterId=${riskRegisterId}`),
      )
      .pipe(catchError((error) => {
        this.toastService.showError(error);
        return of(null);
      }));
  }

  isBayesianResultStale(riskRegisterId: string): Observable<boolean> {
    return this.http
      .get<boolean>(this.getApiUrl(`BayesianAnalysis/IsStale?riskRegisterId=${riskRegisterId}`))
      .pipe(catchError((error) => {
        this.toastService.showError(error);
        return of(false);
      }));
  }
}
